/**
 * Performance.js — Les Marchands de Sable
 *
 * Deferred, non-critical JavaScript for:
 * - Scroll reveal animations via IntersectionObserver
 * - Lazy image blur-up transitions
 * - Deferred resource loading
 * - Debounced scroll/resize handlers
 * - Cart drawer fetch optimization
 *
 * Loaded with `defer` in theme.liquid.
 */

(function () {
  'use strict';

  /* =============================================
     UTILITIES
     ============================================= */

  /**
   * Debounce function — delays execution until after `wait` ms of inactivity.
   * @param {Function} fn
   * @param {number} wait
   * @returns {Function}
   */
  function debounce(fn, wait) {
    var timer;
    return function () {
      var context = this;
      var args = arguments;
      clearTimeout(timer);
      timer = setTimeout(function () {
        fn.apply(context, args);
      }, wait);
    };
  }

  /**
   * Throttle function — executes at most once per `limit` ms.
   * @param {Function} fn
   * @param {number} limit
   * @returns {Function}
   */
  function throttle(fn, limit) {
    var waiting = false;
    return function () {
      if (!waiting) {
        fn.apply(this, arguments);
        waiting = true;
        setTimeout(function () {
          waiting = false;
        }, limit);
      }
    };
  }

  /**
   * Check if user prefers reduced motion.
   * @returns {boolean}
   */
  function prefersReducedMotion() {
    return (
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    );
  }

  /* =============================================
     SCROLL REVEAL ANIMATIONS
     ============================================= */

  function initScrollReveal() {
    if (prefersReducedMotion()) {
      // Immediately reveal all elements when reduced motion is preferred
      var elements = document.querySelectorAll('[data-reveal]');
      for (var i = 0; i < elements.length; i++) {
        elements[i].classList.add('revealed');
        elements[i].style.transition = 'none';
      }
      return;
    }

    var observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -40px 0px'
    };

    var revealObserver = new IntersectionObserver(function (entries) {
      for (var i = 0; i < entries.length; i++) {
        var entry = entries[i];
        if (entry.isIntersecting) {
          entry.target.classList.add('revealed');
          revealObserver.unobserve(entry.target);
        }
      }
    }, observerOptions);

    function observeNewElements(root) {
      var targets = (root || document).querySelectorAll(
        '[data-reveal]:not(.revealed)'
      );
      for (var j = 0; j < targets.length; j++) {
        revealObserver.observe(targets[j]);
      }
    }

    observeNewElements();

    // Re-observe after Shopify section loads/reloads (theme editor)
    document.addEventListener('shopify:section:load', function (event) {
      observeNewElements(event.target);
    });
  }

  /* =============================================
     LAZY IMAGE BLUR-UP
     ============================================= */

  function initLazyImages() {
    var images = document.querySelectorAll('[data-lazy-image]');
    for (var i = 0; i < images.length; i++) {
      var wrapper = images[i];
      var img = wrapper.querySelector('.lazy-image__src');
      if (img && img.complete) {
        wrapper.classList.add('lazy-image--loaded');
      }
    }
  }

  /* =============================================
     DEFERRED HOVER IMAGE LOADING (product cards)
     ============================================= */

  function initDeferredHoverImages() {
    var cards = document.querySelectorAll('[data-product-card]');
    if (!cards.length) return;

    var hoverObserver = new IntersectionObserver(
      function (entries) {
        for (var i = 0; i < entries.length; i++) {
          var entry = entries[i];
          if (entry.isIntersecting) {
            var card = entry.target;
            var deferredImg = card.querySelector('[data-hover-src]');
            if (deferredImg) {
              var src = deferredImg.getAttribute('data-hover-src');
              if (src) {
                deferredImg.setAttribute('src', src);
                deferredImg.removeAttribute('data-hover-src');
              }
            }
            hoverObserver.unobserve(card);
          }
        }
      },
      {
        rootMargin: '200px 0px'
      }
    );

    for (var j = 0; j < cards.length; j++) {
      hoverObserver.observe(cards[j]);
    }
  }

  /* =============================================
     SCROLL PROGRESS BAR
     ============================================= */

  function initScrollProgress() {
    var bar = document.getElementById('scroll-progress');
    if (!bar) return;

    if (prefersReducedMotion()) {
      bar.style.display = 'none';
      return;
    }

    var ticking = false;
    window.addEventListener(
      'scroll',
      function () {
        if (!ticking) {
          window.requestAnimationFrame(function () {
            var scrollTop = window.scrollY;
            var docHeight =
              document.documentElement.scrollHeight - window.innerHeight;
            if (docHeight > 0) {
              bar.style.width = (scrollTop / docHeight) * 100 + '%';
            }
            ticking = false;
          });
          ticking = true;
        }
      },
      { passive: true }
    );
  }

  /* =============================================
     CART DRAWER — FETCH OPTIMIZATION
     ============================================= */

  var cartDrawerInitialized = false;

  function initCartDrawer() {
    if (cartDrawerInitialized) return;
    cartDrawerInitialized = true;

    var cartDrawer = document.querySelector('[data-cart-drawer]');
    var cartOverlay = document.querySelector('[data-cart-drawer-overlay]');
    var cartToggles = document.querySelectorAll('[data-cart-toggle]');
    var cartClose = document.querySelector('[data-cart-drawer-close]');

    if (!cartDrawer) return;

    function openCart() {
      cartDrawer.classList.add('is-open');
      cartDrawer.removeAttribute('inert');
      if (cartOverlay) cartOverlay.classList.add('is-open');
      document.body.style.overflow = 'hidden';
    }

    function closeCart() {
      cartDrawer.classList.remove('is-open');
      cartDrawer.setAttribute('inert', '');
      if (cartOverlay) cartOverlay.classList.remove('is-open');
      document.body.style.overflow = '';
    }

    for (var i = 0; i < cartToggles.length; i++) {
      cartToggles[i].addEventListener('click', openCart);
    }

    if (cartClose) cartClose.addEventListener('click', closeCart);
    if (cartOverlay) cartOverlay.addEventListener('click', closeCart);

    // Bind remove + qty buttons on initial page load
    initCartDrawerEvents();

    document.addEventListener('keydown', function (e) {
      if (
        e.key === 'Escape' &&
        cartDrawer.classList.contains('is-open')
      ) {
        closeCart();
      }
    });
  }

  /**
   * Fetch-based add to cart. Returns a promise with the cart JSON.
   * @param {string|number} variantId
   * @param {number} quantity
   * @returns {Promise}
   */
  function addToCart(variantId, quantity) {
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        id: variantId,
        quantity: quantity || 1
      })
    })
      .then(function (response) {
        return response.json();
      })
      .then(function (data) {
        // Dispatch event so other components can react
        document.dispatchEvent(
          new CustomEvent('cart:updated', { detail: data })
        );
        return data;
      });
  }

  /**
   * Fetch current cart state.
   * @returns {Promise}
   */
  function fetchCart() {
    return fetch('/cart.js', {
      headers: { 'Content-Type': 'application/json' }
    }).then(function (response) {
      return response.json();
    });
  }

  /**
   * Update the cart count badge in the header.
   */
  function updateCartCount() {
    fetchCart().then(function (cart) {
      var badges = document.querySelectorAll('[data-cart-count]');
      for (var i = 0; i < badges.length; i++) {
        badges[i].textContent = cart.item_count;
        if (cart.item_count > 0) {
          badges[i].removeAttribute('hidden');
        } else {
          badges[i].setAttribute('hidden', '');
        }
      }
    });
  }

  /**
   * Refresh the cart drawer by reloading the page section.
   * Uses Shopify section rendering to get fresh HTML.
   */
  function refreshCartDrawer() {
    var oldDrawer = document.querySelector('[data-cart-drawer]');
    if (!oldDrawer) return;

    var wasOpen = oldDrawer.classList.contains('is-open');
    var currentUrl = window.location.href.split('#')[0];

    fetch(currentUrl, { headers: { 'Accept': 'text/html' } })
      .then(function(r) { return r.text(); })
      .then(function(html) {
        var parser = new DOMParser();
        var doc = parser.parseFromString(html, 'text/html');
        var newDrawer = doc.querySelector('[data-cart-drawer]');
        oldDrawer = document.querySelector('[data-cart-drawer]');
        if (newDrawer && oldDrawer) {
          // Replace content but preserve open state
          oldDrawer.replaceWith(newDrawer);
          if (wasOpen) {
            newDrawer.classList.add('is-open');
            newDrawer.removeAttribute('inert');
          }
          // Re-init event listeners
          initCartDrawerEvents();
        }
        // Update cart count badges
        var newBadges = doc.querySelectorAll('[data-cart-count]');
        var oldBadges = document.querySelectorAll('[data-cart-count]');
        oldBadges.forEach(function(old, i) {
          if (newBadges[i]) {
            old.textContent = newBadges[i].textContent;
            if (newBadges[i].hasAttribute('hidden')) {
              old.setAttribute('hidden', '');
            } else {
              old.removeAttribute('hidden');
            }
          }
        });
      });
  }

  function initCartDrawerEvents() {
    var cartDrawer = document.querySelector('[data-cart-drawer]');
    var cartOverlay = document.querySelector('[data-cart-drawer-overlay]');
    if (!cartDrawer) return;

    var closeBtn = cartDrawer.querySelector('[data-cart-drawer-close]');
    if (closeBtn) {
      closeBtn.addEventListener('click', function() {
        cartDrawer.classList.remove('is-open');
        cartDrawer.setAttribute('inert', '');
        if (cartOverlay) cartOverlay.classList.remove('is-open');
        document.body.style.overflow = '';
      });
    }

    // Remove buttons
    cartDrawer.querySelectorAll('[data-cart-remove]').forEach(function(btn) {
      btn.addEventListener('click', function() {
        var item = this.closest('[data-cart-item]');
        var variantId = item ? item.getAttribute('data-variant-id') : null;
        if (!variantId) return;
        fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: variantId, quantity: 0 })
        }).then(function() {
          document.dispatchEvent(new CustomEvent('cart:updated'));
        });
      });
    });

    // Quantity buttons
    cartDrawer.querySelectorAll('[data-qty-minus]').forEach(function(btn) {
      btn.addEventListener('click', function() {
        var input = this.parentElement.querySelector('[data-qty-input]');
        var item = this.closest('[data-cart-item]');
        var variantId = item ? item.getAttribute('data-variant-id') : null;
        var qty = parseInt(input.value) - 1;
        if (qty < 0) qty = 0;
        fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: variantId, quantity: qty })
        }).then(function() {
          document.dispatchEvent(new CustomEvent('cart:updated'));
        });
      });
    });

    cartDrawer.querySelectorAll('[data-qty-plus]').forEach(function(btn) {
      btn.addEventListener('click', function() {
        var input = this.parentElement.querySelector('[data-qty-input]');
        var item = this.closest('[data-cart-item]');
        var variantId = item ? item.getAttribute('data-variant-id') : null;
        var qty = parseInt(input.value) + 1;
        fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: variantId, quantity: qty })
        }).then(function() {
          document.dispatchEvent(new CustomEvent('cart:updated'));
        });
      });
    });
  }

  // Listen for cart updates
  document.addEventListener('cart:updated', function() {
    updateCartCount();
    refreshCartDrawer();
  });

  /* =============================================
     DEBOUNCED RESIZE HANDLER
     ============================================= */

  var resizeCallbacks = [];

  function onResize(callback) {
    resizeCallbacks.push(callback);
  }

  var debouncedResize = debounce(function () {
    for (var i = 0; i < resizeCallbacks.length; i++) {
      resizeCallbacks[i]();
    }
  }, 150);

  window.addEventListener('resize', debouncedResize, { passive: true });

  /* =============================================
     DEFERRED RESOURCE LOADING
     ============================================= */

  /**
   * Load a stylesheet asynchronously.
   * @param {string} href
   */
  function loadStylesheet(href) {
    var link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = href;
    link.media = 'print';
    link.addEventListener('load', function () {
      link.media = 'all';
    });
    document.head.appendChild(link);
  }

  /**
   * Load a script asynchronously.
   * @param {string} src
   * @returns {Promise}
   */
  function loadScript(src) {
    return new Promise(function (resolve, reject) {
      var script = document.createElement('script');
      script.src = src;
      script.defer = true;
      script.addEventListener('load', resolve);
      script.addEventListener('error', reject);
      document.head.appendChild(script);
    });
  }

  /* =============================================
     WILL-CHANGE OPTIMIZATION
     ============================================= */

  /**
   * Only apply will-change when an animation is about to start,
   * then remove it after completion to free compositor memory.
   */
  function initWillChangeOptimization() {
    if (prefersReducedMotion()) return;

    var animatedElements = document.querySelectorAll('.hover-lift, .hover-zoom img, .hover-glow');

    for (var i = 0; i < animatedElements.length; i++) {
      (function (el) {
        el.addEventListener('mouseenter', function () {
          el.style.willChange = 'transform, box-shadow';
        });
        el.addEventListener('mouseleave', function () {
          el.style.willChange = 'auto';
        });
        // Touch support
        el.addEventListener('touchstart', function () {
          el.style.willChange = 'transform, box-shadow';
        }, { passive: true });
        el.addEventListener('touchend', function () {
          el.style.willChange = 'auto';
        }, { passive: true });
      })(animatedElements[i]);
    }
  }

  /* =============================================
     HEADER HIDE-ON-SCROLL-DOWN / SHOW-ON-SCROLL-UP
     ============================================= */

  function initHeaderAutoHide() {
    var header = document.querySelector('.header--sticky');
    if (!header || prefersReducedMotion()) return;

    var lastScrollY = 0;
    var headerHeight = header.offsetHeight;
    var scrollThreshold = 5; // Ignore tiny scroll jitter

    var handleScroll = throttle(function () {
      var currentScrollY = window.scrollY;
      var delta = currentScrollY - lastScrollY;

      // Always show header near the top of the page
      if (currentScrollY < headerHeight * 2) {
        header.classList.remove('header--hidden');
        lastScrollY = currentScrollY;
        return;
      }

      // Scrolling down past threshold — hide header
      if (delta > scrollThreshold) {
        header.classList.add('header--hidden');
      }
      // Scrolling up past threshold — show header
      else if (delta < -scrollThreshold) {
        header.classList.remove('header--hidden');
      }

      lastScrollY = currentScrollY;
    }, 100);

    window.addEventListener('scroll', handleScroll, { passive: true });
  }

  /* =============================================
     CART COUNT BOUNCE ANIMATION
     ============================================= */

  function initCartCountBounce() {
    document.addEventListener('cart:updated', function () {
      var badges = document.querySelectorAll('[data-cart-count]');
      for (var i = 0; i < badges.length; i++) {
        var badge = badges[i];
        badge.classList.remove('is-bouncing');
        // Force reflow to restart animation
        void badge.offsetWidth;
        badge.classList.add('is-bouncing');
      }
    });
  }

  /* =============================================
     FOCUS TRAP FOR MODALS (cart drawer, mobile nav)
     ============================================= */

  function trapFocus(container) {
    var focusable = container.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
    if (!focusable.length) return;

    var firstFocusable = focusable[0];
    var lastFocusable = focusable[focusable.length - 1];

    function handleTab(e) {
      if (e.key !== 'Tab') return;

      if (e.shiftKey) {
        if (document.activeElement === firstFocusable) {
          e.preventDefault();
          lastFocusable.focus();
        }
      } else {
        if (document.activeElement === lastFocusable) {
          e.preventDefault();
          firstFocusable.focus();
        }
      }
    }

    container.addEventListener('keydown', handleTab);
    firstFocusable.focus();

    return function removeTrap() {
      container.removeEventListener('keydown', handleTab);
    };
  }

  /* =============================================
     ENHANCED CART DRAWER WITH FOCUS TRAP
     ============================================= */

  function initCartDrawerFocusTrap() {
    var cartDrawer = document.querySelector('[data-cart-drawer]');
    if (!cartDrawer) return;

    var removeTrap = null;
    var previousFocus = null;

    var observer = new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        if (mutations[i].attributeName === 'class') {
          var isOpen = cartDrawer.classList.contains('is-open');
          if (isOpen) {
            previousFocus = document.activeElement;
            removeTrap = trapFocus(cartDrawer);
          } else {
            if (removeTrap) removeTrap();
            if (previousFocus) previousFocus.focus();
          }
        }
      }
    });

    observer.observe(cartDrawer, { attributes: true });
  }

  /* =============================================
     HERO PARALLAX (subtle)
     ============================================= */

  function initHeroParallax() {
    var heroImage = document.querySelector('.hero__image:not(.hero__image--mobile)');
    if (!heroImage || prefersReducedMotion()) return;

    var hero = heroImage.closest('.hero');
    if (!hero) return;

    var ticking = false;

    window.addEventListener('scroll', function () {
      if (!ticking) {
        window.requestAnimationFrame(function () {
          var rect = hero.getBoundingClientRect();
          // Only animate when hero is visible
          if (rect.bottom > 0 && rect.top < window.innerHeight) {
            var scrolled = window.scrollY;
            var rate = scrolled * 0.15;
            heroImage.style.transform = 'translate3d(0, ' + rate + 'px, 0) scale(1.05)';
          }
          ticking = false;
        });
        ticking = true;
      }
    }, { passive: true });
  }

  /* =============================================
     PAGE LOAD TRANSITION
     ============================================= */

  function initPageTransition() {
    var main = document.getElementById('main');
    if (main && !prefersReducedMotion()) {
      main.classList.add('template-enter');
    }
  }

  /* =============================================
     INITIALIZATION
     ============================================= */

  function init() {
    initScrollReveal();
    initLazyImages();
    initDeferredHoverImages();
    initScrollProgress();
    initCartDrawer();
    initWillChangeOptimization();
    initHeaderAutoHide();
    initCartCountBounce();
    initCartDrawerFocusTrap();
    initHeroParallax();
    initPageTransition();
  }

  // Run on DOMContentLoaded or immediately if already loaded
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Expose utilities for other scripts
  window.MDS = window.MDS || {};
  window.MDS.performance = {
    debounce: debounce,
    throttle: throttle,
    onResize: onResize,
    loadStylesheet: loadStylesheet,
    loadScript: loadScript,
    addToCart: addToCart,
    fetchCart: fetchCart,
    updateCartCount: updateCartCount,
    trapFocus: trapFocus
  };
})();
